<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AutomationPreviewAccordion from './AutomationPreviewAccordion.vue';

const emit = defineEmits(['select']);

const { t } = useI18n();

const dialogRef = ref(null);
const activeType = ref('macro');
const searchQuery = ref('');

const macros = useMapGetter('macros/getMacros');
// The cannedResponse Vuex module isn't namespaced, unlike macros.
const cannedResponses = useMapGetter('getCannedResponses');

// Automation rules are excluded: they fire on their own server-side
// triggers, so there is nothing for an agent to run from a SOP step.
const TYPES = ['macro', 'canned_response'];

const itemsForType = computed(() => {
  if (activeType.value === 'canned_response') {
    return cannedResponses.value.map(canned => ({
      id: canned.id,
      name: canned.short_code,
    }));
  }
  return macros.value.map(macro => ({ id: macro.id, name: macro.name }));
});

const filteredItems = computed(() => {
  const query = searchQuery.value.toLowerCase();
  if (!query) return itemsForType.value;
  return itemsForType.value.filter(item =>
    item.name?.toLowerCase().includes(query)
  );
});

const open = () => {
  searchQuery.value = '';
  dialogRef.value.open();
};

const selectItem = item => {
  emit('select', { type: activeType.value, id: item.id, name: item.name });
  dialogRef.value.close();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('SOPS.PICKER.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
  >
    <div class="flex flex-col gap-3">
      <div class="flex gap-1 rounded-lg bg-n-alpha-black2 p-1">
        <button
          v-for="type in TYPES"
          :key="type"
          class="flex-1 rounded-md px-2 py-1.5 text-xs transition-colors"
          :class="
            activeType === type
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          type="button"
          @click="activeType = type"
        >
          {{ t(`SOPS.AUTOMATION_TYPES.${type.toUpperCase()}`) }}
        </button>
      </div>
      <Input
        v-model="searchQuery"
        :placeholder="t('SOPS.PICKER.SEARCH_PLACEHOLDER')"
      />
      <div class="flex max-h-80 min-h-40 flex-col gap-1 overflow-y-auto">
        <AutomationPreviewAccordion
          v-for="item in filteredItems"
          :id="item.id"
          :key="item.id"
          :type="activeType"
          :name="item.name"
          class="rounded-lg px-3 py-2 hover:bg-n-alpha-black2"
        >
          <template #name>
            <button
              class="min-w-0 flex-1 whitespace-pre-wrap break-words text-left text-sm text-n-slate-12"
              type="button"
              @click="selectItem(item)"
            >
              {{ item.name }}
            </button>
          </template>
        </AutomationPreviewAccordion>
        <p
          v-if="!filteredItems.length"
          class="mb-0 py-6 text-center text-sm text-n-slate-10"
        >
          {{ t('SOPS.PICKER.EMPTY') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
